import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiSortDirection } from '@euler/model/bi/query';
import { ArrowDown, ArrowUp } from 'lucide-react-native';
import { memo } from 'react';
import { TextInput, View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import { EditorSection } from './EditorSection';
import { biFieldLabel } from './functions/biEditorLabels';
import {
  BiEditorQuery,
  biEditorQueryKeys,
  kBiEditorMaxLimit,
} from './functions/biEditorState';

/** The label of a result key: its field's, with `__grain`/`__agg` dropped. */
function keyLabel(catalog: BiDatasetCatalog | null, key: string) {
  return biFieldLabel(catalog, key.split('__')[0]);
}

/** One sort key (tap again to flip, a third time to clear) and a limit. */
export const SortLimitSection = memo(
  ({
    query,
    catalog,
    onChange,
  }: {
    query: BiEditorQuery;
    catalog: BiDatasetCatalog | null;
    onChange: (next: BiEditorQuery) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const current = query.sort[0];
    const cycle = (key: string) => {
      if (current?.key !== key) {
        onChange({
          ...query,
          sort: [{ key, dir: BiSortDirection.Desc }],
        });
        return;
      }
      if (current.dir === BiSortDirection.Desc) {
        onChange({
          ...query,
          sort: [{ key, dir: BiSortDirection.Asc }],
        });
        return;
      }
      onChange({ ...query, sort: [] });
    };
    const setLimit = (text: string) => {
      const value = parseInt(text.replace(/\D/g, ''), 10);
      onChange({
        ...query,
        limit: Number.isFinite(value)
          ? Math.min(Math.max(value, 1), kBiEditorMaxLimit)
          : undefined,
      });
    };
    const keys = biEditorQueryKeys(query);
    return (
      <EditorSection title="排序与数量">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
          {keys.map(key => (
            <BiChip
              key={key}
              label={keyLabel(catalog, key)}
              icon={
                current?.key === key
                  ? current.dir === BiSortDirection.Desc
                    ? ArrowDown
                    : ArrowUp
                  : undefined
              }
              selected={current?.key === key}
              onPress={() => cycle(key)}
            />
          ))}
          {!keys.length ? (
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              先选指标或维度
            </Typography.Text>
          ) : null}
        </View>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
        >
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            最多显示
          </Typography.Text>
          <TextInput
            accessibilityLabel="最多显示的行数"
            value={query.limit === undefined ? '' : String(query.limit)}
            onChangeText={setLimit}
            keyboardType="number-pad"
            placeholder="默认"
            placeholderTextColor={aiColors.mutedForeground}
            style={{
              width: rpx(72),
              height: rpx(32),
              paddingHorizontal: rpx(10),
              borderRadius: rpx(10),
              backgroundColor: aiColors.input,
              color: aiColors.foreground,
              fontSize: rpx(13),
            }}
          />
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            行
          </Typography.Text>
        </View>
      </EditorSection>
    );
  },
);
